import { cn } from './cn';

/** The road code (DESIGN_SYSTEM.md §2): every status is a colour AND a road-sign shape. */
export type RoadCode = 'go' | 'caution' | 'info' | 'hazard' | 'offline';
/** One sign per code, plus `parked`: the info square with a P. */
export type SignName = RoadCode | 'parked';

export const ROAD_CODES: readonly RoadCode[] = ['go', 'caution', 'info', 'hazard', 'offline'];

/** The code a sign belongs to (parked is info). */
export const codeOf = (sign: SignName): RoadCode => (sign === 'parked' ? 'info' : sign);

const WHITE = 'var(--vlm-brand-white)';
const ASPHALT = 'var(--vlm-brand-asphalt)';
const RING = 'var(--vlm-marker-ring)';

// Drawn for VLM in brand/preview (§06 road code, §07 maps). Never swap in a stock icon.
const SIGNS: Record<SignName, string> = {
  go: `<circle cx="12" cy="12" r="10.5" style="fill:var(--vlm-go)"/><path d="M12 5.6l5.2 6.3h-3.3v6.5h-3.8v-6.5H6.8z" style="fill:${WHITE}"/>`,
  caution: `<path d="M12 2.6l10.3 17.9H1.7z" style="fill:var(--vlm-caution);stroke:${ASPHALT}" stroke-width="1.3" stroke-linejoin="round"/><path d="M12 9.2v5.2" style="stroke:${ASPHALT}" stroke-width="2.2" stroke-linecap="round"/><circle cx="12" cy="17.3" r="1.3" style="fill:${ASPHALT}"/>`,
  info: `<rect x="2" y="2" width="20" height="20" rx="4.5" style="fill:var(--vlm-info)"/><circle cx="12" cy="7.4" r="1.5" style="fill:${WHITE}"/><path d="M12 11v6.4" style="stroke:${WHITE}" stroke-width="2.4" stroke-linecap="round"/>`,
  parked: `<rect x="2" y="2" width="20" height="20" rx="4.5" style="fill:var(--vlm-info)"/><path d="M9.4 17.5V6.8h3.7a3.2 3.2 0 0 1 0 6.4H9.4" fill="none" style="stroke:${WHITE}" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>`,
  hazard: `<path d="M15.9 2.6L21.4 8.1V15.9L15.9 21.4H8.1L2.6 15.9V8.1L8.1 2.6z" style="fill:var(--vlm-hazard)"/><path d="M12 6.8v6.4" style="stroke:${WHITE}" stroke-width="2.4" stroke-linecap="round"/><circle cx="12" cy="16.9" r="1.4" style="fill:${WHITE}"/>`,
  offline: `<circle cx="12" cy="12" r="8.4" fill="none" style="stroke:var(--vlm-offline)" stroke-width="3"/><path d="M6.5 17.5l11-11" style="stroke:var(--vlm-offline)" stroke-width="2.4" stroke-linecap="round"/>`,
};

// The same signs as map markers: a Lane White ring so they read on any base map. viewBox -14 -14 28 28.
const MARKERS: Record<SignName, string> = {
  go: `<circle r="12.5" style="fill:${RING}"/><circle r="10" style="fill:var(--vlm-go)"/><path d="M0 -6.4l5 6.2H1.8V6H-1.8V-0.2H-5z" style="fill:${WHITE}"/>`,
  caution: `<path d="M0 -13.2L13 9.6H-13z" style="fill:${RING};stroke:${RING}" stroke-width="2.5" stroke-linejoin="round"/><path d="M0 -9.4L9.8 7.6H-9.8z" style="fill:var(--vlm-caution);stroke:${ASPHALT}" stroke-width="1" stroke-linejoin="round"/><path d="M0 -3.6v4.6" style="stroke:${ASPHALT}" stroke-width="2" stroke-linecap="round"/><circle cy="4.2" r="1.15" style="fill:${ASPHALT}"/>`,
  info: `<rect x="-12" y="-12" width="24" height="24" rx="6" style="fill:${RING}"/><rect x="-9.6" y="-9.6" width="19.2" height="19.2" rx="4" style="fill:var(--vlm-info)"/><circle cy="-3.7" r="1.25" style="fill:${WHITE}"/><path d="M0 -0.6v4.9" style="stroke:${WHITE}" stroke-width="2" stroke-linecap="round"/>`,
  parked: `<rect x="-12" y="-12" width="24" height="24" rx="6" style="fill:${RING}"/><rect x="-9.6" y="-9.6" width="19.2" height="19.2" rx="4" style="fill:var(--vlm-info)"/><path d="M-2.6 5.4V-5.2H0.9a3 3 0 0 1 0 6H-2.6" fill="none" style="stroke:${WHITE}" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/>`,
  hazard: `<path d="M5.4 -13L13 -5.4V5.4L5.4 13H-5.4L-13 5.4V-5.4L-5.4 -13z" style="fill:${RING}"/><path d="M4.3 -10.4L10.4 -4.3V4.3L4.3 10.4H-4.3L-10.4 4.3V-4.3L-4.3 -10.4z" style="fill:var(--vlm-hazard)"/><path d="M0 -5.6v5.4" style="stroke:${WHITE}" stroke-width="2.2" stroke-linecap="round"/><circle cy="4.4" r="1.25" style="fill:${WHITE}"/>`,
  offline: `<circle r="11" style="fill:${RING}"/><circle r="7.4" fill="none" style="stroke:var(--vlm-offline)" stroke-width="3"/>`,
};

/** The destination: the red pin from the logo. viewBox 0 0 32 42. */
export const PIN_SVG = `<path d="M16 40.5S3.2 28.6 3.2 17.4a12.8 12.8 0 0 1 25.6 0C28.8 28.6 16 40.5 16 40.5z" style="fill:var(--vlm-brand-red);stroke:${WHITE}" stroke-width="2.6"/><circle cx="16" cy="17.2" r="5" style="fill:${WHITE}"/>`;

export interface RoadSignProps {
  sign: SignName;
  /** px — 13–14 in pills and chips, 20 in rows, 26 in alerts */
  size?: number;
  /** An accessible name; leave it out when a visible label already says the same. */
  label?: string;
  className?: string;
}

/** A road-code sign. Signs never mirror in RTL. */
export function RoadSign({ sign, size = 14, label, className }: RoadSignProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={cn('shrink-0', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      dangerouslySetInnerHTML={{ __html: SIGNS[sign] }}
    />
  );
}

/** The destination pin. `height` in px; the width follows (32 : 42). */
export function Pin({ height = 32, className, label }: { height?: number; className?: string; label?: string }) {
  return (
    <svg
      viewBox="0 0 32 42"
      width={Math.round((height * 32) / 42)}
      height={height}
      className={cn('shrink-0', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      dangerouslySetInnerHTML={{ __html: PIN_SVG }}
    />
  );
}

export interface MarkerOptions {
  /** Degrees clockwise from north. Turns the go arrow; the other signs stay upright. */
  heading?: number | null;
  /** The selected vehicle: a red halo (the waypoint you are looking at). */
  selected?: boolean;
  /** 'once' — a position report just arrived; 'loop' — an open alarm. Never an idle loop. */
  ping?: 'once' | 'loop' | null;
  /** px, default 28 */
  size?: number;
}

/**
 * A map marker as SVG markup, for an HTML marker (MapLibre). The box is the sign; the halo and the
 * ping draw outside it (overflow visible), so they never widen the hit area.
 */
export function markerSvg(sign: SignName, { heading = null, selected = false, ping = null, size = 28 }: MarkerOptions = {}): string {
  const code = codeOf(sign);
  const turn = sign === 'go' && heading !== null && Number.isFinite(heading) ? ` transform="rotate(${Math.round(heading)})"` : '';
  const halo = selected ? '<circle r="21" class="vlm-marker-halo"/>' : '';
  const ring = ping ? `<circle r="14" class="vlm-marker-ping${ping === 'loop' ? ' is-loop' : ''}" style="stroke:var(--vlm-${code})"/>` : '';
  return `<svg class="vlm-marker" viewBox="-14 -14 28 28" width="${size}" height="${size}" overflow="visible" aria-hidden="true" focusable="false">${halo}${ring}<g${turn}>${MARKERS[sign]}</g></svg>`;
}
